import type { QueryClient } from '@tanstack/react-query';

/**
 * React Query hace prefix-match por SEGMENTO del queryKey: invalidar `['servers']` NO alcanza a
 * `['servers-list']` ni `['servers-online']` porque el primer segmento es otro string. Eso dejaba
 * el dashboard, el wizard de deploy y la pantalla de infraestructura con datos viejos después de
 * cualquier alta/baja/cambio de estado, hasta recargar la página a mano (2026-09-23).
 *
 * Familias que no se derivan por guión (misma API, otro nombre de key) — si se agrega una vista
 * derivada nueva con un nombre que no arranque con `<entidad>-`, va acá o queda sin refrescar.
 */
const RELATED_FAMILIES: Record<string, string[]> = {
  applications: ['apps'],
  approvals: ['pending-approvals'],
  backups: ['backup'],
  deploys: ['deploy', 'recent-deploys', 'latest-deploy', 'infra'],
  registries: ['images', 'app-images', 'app-image-tags'],
  scopes: ['scope'],
  servers: ['infra'],
  users: ['user'],
};

/**
 * Invalida una entidad junto con todas sus vistas derivadas (`<entidad>`, `<entidad>-*` y las
 * familias emparentadas de arriba). Usar SIEMPRE esto en el `onSuccess` de una mutación en vez de
 * `invalidateQueries({ queryKey: [...] })` suelto: la tabla de la pantalla actual no es la única
 * vista de ese dato.
 */
export function invalidateEntity(queryClient: QueryClient, ...entities: string[]): Promise<void> {
  const heads = entities.flatMap((entity) => [entity, ...(RELATED_FAMILIES[entity] ?? [])]);

  return queryClient.invalidateQueries({
    predicate: (query) => {
      const head = String(query.queryKey[0] ?? '');
      return heads.some((h) => head === h || head.startsWith(`${h}-`));
    },
  });
}

/**
 * Nombre de entidad del backend (primer segmento de la ruta de la API, p. ej. `infrastructure`)
 * → familia de queryKeys de la UI, cuando no coinciden.
 */
const SERVER_ENTITY_ALIASES: Record<string, string> = {
  infrastructure: 'infra',
};

/**
 * Invalida las vistas de una entidad que el backend avisó como modificada (evento WebSocket
 * `entity.changed`, canal `entities`) — sea por una acción de este usuario, de otra pestaña/usuario
 * o de un proceso en background (p. ej. un sync de registry que termina).
 */
export function invalidateServerEntity(queryClient: QueryClient, entity: string): Promise<void> {
  return invalidateEntity(queryClient, SERVER_ENTITY_ALIASES[entity] ?? entity);
}
